import {getRefServiceStatusSeverity, LocationStatusFacetEntity} from "@ots-frontend/ots-data";
import {getRefServiceStatusColor} from "../util/ref-service-status-ui-util";

export interface OpsFacetModeStatus {
  mode: string;
  status: string;
  color: string;
  asOfDttm: number;
}

export interface OpsFacetStatus {
  facet: string;
  status: string;
  color: string;
  modes: OpsFacetModeStatus[];
}

export interface OpsServiceTypeStatus {
  serviceTypeId: string;
  serviceTypeName: string;
  facets: Record<string, OpsFacetStatus>;
  overallStatus: string;
  overallColor: string;
}

export interface OpsLocationStatus {
  siteId: string;
  siteName?: string;
  latCoord?: number;
  longCoord?: number;
  geofenceRadius?: number;
  overallStatus: string;
  overallColor: string;
  serviceTypes: Record<string, OpsServiceTypeStatus>;
  rows: LocationStatusFacetEntity[];
}

function worstStatus(a: string, b: string): string {
  return getRefServiceStatusSeverity(a) <= getRefServiceStatusSeverity(b) ? a : b;
}

export function buildOpsLocationStatuses(
  rows: LocationStatusFacetEntity[]
): OpsLocationStatus[] {
  const byLocation = new Map<string, { location: OpsLocationStatus }>();

  for (const row of rows) {
    const status = row.status;
    if (!byLocation.has(row.siteId)) {
      byLocation.set(row.siteId, {
        location: {
          siteId: row.siteId,
          siteName: row.siteName,
          latCoord: row.siteLatCoord,
          longCoord: row.siteLongCoord,
          geofenceRadius: row.siteGeofenceExtension,
          overallStatus: status,
          overallColor: getRefServiceStatusColor(status),
          serviceTypes: {},
          rows: [],
        },
      });
    }
    const wrapper = byLocation.get(row.siteId)!;
    const location = wrapper.location;
    location.rows.push(row);

    // upsert service type
    const serviceTypeId = row.serviceTypeId
    let serviceType = location.serviceTypes[serviceTypeId];
    if (!serviceType) {
      serviceType = {
        serviceTypeId: serviceTypeId,
        serviceTypeName: row.serviceTypeName,
        facets: {},
        overallColor: getRefServiceStatusColor(status),
        overallStatus: status,
      };
      location.serviceTypes[serviceTypeId] = serviceType;
    } else {
      const worstFacetStatus = worstStatus(serviceType.overallStatus, status);
      serviceType.overallStatus = worstFacetStatus;
      serviceType.overallColor = getRefServiceStatusColor(worstFacetStatus);
    }

    // upsert facet
    const facetKey = row.statusFacet || "UNKNOWN";
    let facetStatus = serviceType.facets[facetKey];
    if (!facetStatus) {
      facetStatus = {
        facet: facetKey,
        status: status,
        color: getRefServiceStatusColor(status),
        modes: [],
      };
      serviceType.facets[facetKey] = facetStatus;
    } else {
      // aggregate worst status for facet
      const worstFacetStatus = worstStatus(facetStatus.status, status);
      facetStatus.status = worstFacetStatus;
      facetStatus.color = getRefServiceStatusColor(worstFacetStatus);
    }

    // append mode-level detail (snow: often PLOW and SALT)
    facetStatus.modes.push({
      mode: row.serviceMode,
      status,
      color: getRefServiceStatusColor(status),
      asOfDttm: row.asOfDttm,
    });

    // aggregate overall site status (worst across all facets/modes)
    const worstSiteStatus = worstStatus(location.overallStatus, status);
    location.overallStatus = worstSiteStatus;
    location.overallColor = getRefServiceStatusColor(worstSiteStatus);
  }

  return Array.from(byLocation.values()).map((w) => w.location);
}
